import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Colors } from 'context';
import { careerType } from 'services/careerType';

/**
 * Мой уровень против требуемого — шкалой, а не строкой (ТЗ 0070).
 *
 * 🔴 Шкала это ВТОРОЙ канал: точные числа остаются словами под ней. Правило
 * общее для раздела (см. `ClusterProgressRing` и `CareerReadinessGrid`) —
 * цветом и длиной пользуются, чтобы поймать взглядом, а называет положение
 * дел по-прежнему текст.
 *
 * 🔴 Делений столько, сколько требует профессия, а не всегда пять. У навыка с
 * требуемым уровнем 2 три пустых хвостовых деления читались бы как «мне ещё
 * далеко», хотя цель уже закрыта. Потолок 5 — на случай мусора в справочнике.
 *
 * ⚠️ `myLevel === null` — это «навыка нет вовсе», а не «уровень ноль»; писать
 * «Ваш уровень 0» значит утверждать то, чего в профиле не записано.
 */
interface ISkillLevelMeter {
  myLevel: number | null;
  requiredLevel: number;
}

export const SkillLevelMeter = memo(({ myLevel, requiredLevel }: ISkillLevelMeter) => {
  const { t } = useTranslation();

  const total = Math.min(5, Math.max(requiredLevel, myLevel ?? 0, 1));
  const mine = myLevel ?? 0;
  const words =
    myLevel === null
      ? t('careerCenter.map.levelsNone', { required: requiredLevel })
      : t('careerCenter.map.levels', { mine, required: requiredLevel });

  return (
    <Root>
      <Bars
        role="progressbar"
        aria-label={t('careerCenter.map.levelLabel')}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={mine}
        aria-valuetext={words}>
        {Array.from({ length: total }, (_, index) => index + 1).map((step) => (
          <Bar
            key={step}
            aria-hidden
            $filled={step <= mine}
            /* Отметка требуемого уровня: без неё шкала показывает «сколько
               есть», но не «сколько нужно». */
            $target={step === requiredLevel}
          />
        ))}
      </Bars>
      <Words>{words}</Words>
    </Root>
  );
});

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Bars = styled.div`
  display: flex;
  gap: 4px;
  max-width: 260px;
`;

const Bar = styled.span<{ $filled: boolean; $target: boolean }>`
  flex: 1 1 28px;
  height: 8px;
  border-radius: 4px;
  background: ${({ $filled }) => ($filled ? Colors.signal_green : Colors.gray)};
  outline: ${({ $target }) => ($target ? `1px solid ${Colors.black}` : 'none')};
  outline-offset: 1px;
`;

const Words = styled.span`
  ${careerType.meta()};
  color: ${Colors.gray_dark};
`;
